import { ParsedUrlQuery } from 'querystring'
import { isUnitedStates } from '@/services/UserRegionService'
import { Maybe, ReservationStatus, ReservationVenueAddress } from '@/types/codegen-federation'
import { CustomReservationSeat } from '@/views/TicketCheckoutViews/SeatMapView/components/SeatMapPage'
import { TicketsAnalyticsPayload } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { ShowtimesVenue } from '../ShowtimesView/hooks/useVenueSearch/useVenueSearch'

export function getHoursMinutes(minutes: number, locale: string) {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  const formattedHours = new Intl.NumberFormat(locale).format(hours)
  const formattedMinutes = new Intl.NumberFormat(locale).format(remainingMinutes)
  if (hours) {
    return `${formattedHours}h ${formattedMinutes}m`
  }

  return `${formattedMinutes}m`
}

export const getDiscountCodes = ({ query }: { query: ParsedUrlQuery }) => {
  const discountsQuery = query?.discounts
  if (discountsQuery && typeof discountsQuery === 'string') return discountsQuery.split(',')
  return []
}

export const getCodes = ({ query }: { query: ParsedUrlQuery }) => {
  const codesQuery = query?.codes
  if (codesQuery && typeof codesQuery === 'string') return codesQuery.split(',')
  return []
}

export const getFlags = ({ query }: { query: ParsedUrlQuery }) => {
  const flagsQuery = query?.flags

  if (flagsQuery && typeof flagsQuery === 'string') return flagsQuery.split(',')
  return []
}
export const getSelectedSeatRate = (
  seatId: string,
  seats: Map<string, CustomReservationSeat>,
  guildTicketAvailable: boolean,
) => {
  let selectedRate = seats.get(seatId)?.selectedRate

  // hijack the discountPrice if guild tickets are available, since they're not currently accounted for in the data
  if (guildTicketAvailable && selectedRate?.discountedPrice) {
    selectedRate = {
      ...selectedRate,
      discountedPrice: {
        ...selectedRate.discountedPrice,
        currencyAmount: 0,
      },
    }
  }
  return selectedRate
}
export const formatAddress = (address?: Maybe<ReservationVenueAddress>) => {
  if (!address) return ''
  const { line, countryCode, state, city } = address
  if (isUnitedStates(countryCode)) return `${line}, ${city}, ${state}`
  return `${line}, ${city}, ${countryCode}`
}

export const isStatusValid = (status: string) => {
  const invalidStatuses = new Set(['COMPLETE', 'CANCELLED'])
  return !invalidStatuses.has(status)
}

export const isEmailValid = (email: string) => {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
  return emailRegex.test(email)
}

export const getLanguageName = (locale: string): string => {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' })
    return displayNames.of(locale) as string
  } catch (err) {
    return locale
  }
}

export const getLanguageNameUsingUserLocale = (locale: string, lang: string): string => {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' })
    return displayNames.of(lang) as string
  } catch (err) {
    return locale
  }
}

export const generateTrackingPayload = (
  showTime: ShowtimesVenue['showtimes'][number],
  analyticsPayload: TicketsAnalyticsPayload,
  reservationId?: string | undefined,
) => {
  return {
    ...analyticsPayload,
    reservationId: reservationId ?? null,
    startTime: showTime.utcStartTime,
    showtimeId: showTime?.id,
  }
}

export const inoperationalReservationStatuses: Set<ReservationStatus['reference']> = new Set(['COMPLETE', 'CANCELLED'])

export const delayedReservationStatuses: Set<ReservationStatus['reference']> = new Set([
  'PAYMENT_COMPLETED',
  'ATOM_ORDER_FULFILLED',
  'CANCELLATION_PENDING',
  'REBOOKING',
])

export enum Flow {
  ANGEL = 'angel',
  GUILD = 'guild',
  GUILD_SHARE = 'guild-share',
  GUILD_GIFT = 'guild-gift',
  GIFT_TICKETS = 'gift-tickets',
  GIFT_GUILD_TICKETS = 'gift-guild-tickets',
  GROUP_TICKETS = 'group-tickets',
}
